/**
 * explorationStore — 探索笔记全局 Store（Zustand）
 *
 * Sprint 4 升级核心：从"状态标记"升级为"投研叙事"
 *
 * 每个决策 = 一个 NoteRecord 对象，包含：
 * - entity（节点/公司）+ entityType
 * - status（确信/观察/跳过）+ reason（一句话理由）
 * - tags（催化剂/估值/技术领先/产业地位）
 * - timestamp
 * - stockPrice（记录时的股价）
 *
 * 派生状态（Computed）：
 * - watchlistIds / convictionIds / exploredIds — 用于 O(1) 查询
 * - allNotes — 所有笔记按时间倒序
 *
 * 持久化到 localStorage。
 */
import { create } from 'zustand';

const STORAGE_KEY = 'aic:notes_v2'; // v2 = schema 升级

export type NoteEntityType = 'node' | 'company';
export type NoteStatus = 'conviction' | 'watchlist' | 'skipped' | 'explored';
export type NoteTag = 'catalyst' | 'valuation' | 'tech_lead' | 'industry_position';

export interface NoteRecord {
  id: string;                  // 唯一 ID（uuid）
  entityId: string;            // 节点 ID 或公司 ID
  entityType: NoteEntityType;  // 实体类型
  status: NoteStatus;          // 决策类型
  reason: string;              // 一句话理由
  tags: NoteTag[];             // 标签（多选）
  timestamp: number;            // 记录时间（ms）
  stockPrice?: number;          // 记录时股价
  stockPriceCurrency?: string;  // 股价单位（CNY/USD）
}

interface ExplorationState {
  // 已勘察节点（用户查看过详情，不需要笔记）
  explored: string[];
  // 所有笔记（确信/观察/跳过，带理由+标签）
  notes: NoteRecord[];

  // === 动作 ===
  markExplored: (entityId: string) => void;
  addNote: (note: Omit<NoteRecord, 'id' | 'timestamp'>) => void;
  updateNote: (id: string, updates: Partial<Pick<NoteRecord, 'reason' | 'tags' | 'stockPrice'>>) => void;
  removeNote: (id: string) => void;
  toggleWatchlist: (entityId: string, entityType: NoteEntityType) => void;
  clearSelection: (entityId: string, entityType: NoteEntityType) => void;
  resetAll: () => void;

  // === 派生查询（getters）===
  getStatus: (entityId: string) => NoteStatus | null;
  getNote: (entityId: string, entityType: NoteEntityType) => NoteRecord | null;
  getNotesByEntity: (entityId: string) => NoteRecord[];
  getConvictionNotes: () => NoteRecord[];
  getAllNotes: () => NoteRecord[];
  hasNote: (entityId: string) => boolean;
}

const EMPTY_STATE = { explored: [] as string[], notes: [] as NoteRecord[] };

function generateId(): string {
  return `note_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function loadData() {
  if (typeof window === 'undefined') return EMPTY_STATE;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_STATE;
    const parsed = JSON.parse(raw);
    return {
      explored: Array.isArray(parsed.explored) ? parsed.explored.filter((x: unknown) => typeof x === 'string') : [],
      notes: Array.isArray(parsed.notes) ? parsed.notes.filter((n: unknown) =>
        typeof n === 'object' && n !== null && typeof (n as NoteRecord).id === 'string'
      ) : [],
    };
  } catch {
    return EMPTY_STATE;
  }
}

function saveData(data: { explored: string[]; notes: NoteRecord[] }) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, schemaVersion: 2 }));
  } catch (err) {
    console.warn('explorationStore: 保存失败', err);
  }
}

export const useExplorationStore = create<ExplorationState>((set, get) => ({
  ...loadData(),

  markExplored: (entityId) => {
    if (get().explored.includes(entityId)) return;
    set((state) => {
      const next = { ...state, explored: [...state.explored, entityId] };
      saveData(next);
      return next;
    });
  },

  addNote: (noteData) => {
    const note: NoteRecord = {
      ...noteData,
      id: generateId(),
      timestamp: Date.now(),
    };
    set((state) => {
      const next = { ...state, notes: [note, ...state.notes] };
      saveData(next);
      return next;
    });
  },

  updateNote: (id, updates) => {
    set((state) => {
      const next = { ...state, notes: state.notes.map(n => n.id === id ? { ...n, ...updates } : n) };
      saveData(next);
      return next;
    });
  },

  removeNote: (id) => {
    set((state) => {
      const next = { ...state, notes: state.notes.filter(n => n.id !== id) };
      saveData(next);
      return next;
    });
  },

  toggleWatchlist: (entityId, entityType) => {
    const state = get();
    const existing = state.notes.find(n => n.entityId === entityId && n.entityType === entityType);
    if (existing) {
      if (existing.status === 'watchlist') {
        // 已在观察列表 → 移除
        set((s) => {
          const next = { ...s, notes: s.notes.filter(n => n.id !== existing.id) };
          saveData(next);
          return next;
        });
      } else if (existing.status === 'conviction') {
        // conviction 不降级为 watchlist（保护用户的确信判断）
        return;
      } else {
        set((s) => {
          const next = {
            ...s,
            notes: s.notes.map(n => n.id === existing.id
              ? { ...n, status: 'watchlist' as const, timestamp: Date.now() }
              : n),
          };
          saveData(next);
          return next;
        });
      }
    } else {
      get().addNote({ entityId, entityType, status: 'watchlist', reason: '', tags: [], stockPrice: undefined });
    }
  },

  clearSelection: (entityId, entityType) => {
    set((state) => {
      const next = {
        explored: state.explored.filter(id => id !== entityId),
        notes: state.notes.filter(n => !(n.entityId === entityId && n.entityType === entityType)),
      };
      saveData(next);
      return next;
    });
  },

  resetAll: () => {
    set((state) => {
      const next = { ...state, ...EMPTY_STATE };
      saveData(next);
      return next;
    });
  },

  // === 派生查询 ===
  getStatus: (entityId) => {
    const state = get();
    const note = state.notes.find(n => n.entityId === entityId);
    if (note) return note.status;
    if (state.explored.includes(entityId)) return 'explored' as NoteStatus;
    return null;
  },

  getNote: (entityId, entityType) => {
    return get().notes.find(n => n.entityId === entityId && n.entityType === entityType) ?? null;
  },

  getNotesByEntity: (entityId) => {
    return get().notes.filter(n => n.entityId === entityId);
  },

  getConvictionNotes: () => {
    return get().notes.filter(n => n.status === 'conviction');
  },

  getAllNotes: () => {
    return [...get().notes].sort((a, b) => b.timestamp - a.timestamp);
  },

  hasNote: (entityId) => {
    return get().notes.some(n => n.entityId === entityId);
  },
}));
